import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Select } from "@resideo/blueprint-react";
import type { ThermostatConfigurationResponseV2 } from "@resideo/web-device-services-api-client";
import { chevronLeftIcon, informationLineIcon } from "@/assets/icons";
import styles from "./EditControls.module.css";
import { EditControlsRow } from "./components/EditControlsRow/EditControlsRow";
import { thermostatBatchService } from "../../api/services/thermostatBatchService";
import { manageDevicesService } from "../../api/services/manageDevicesServices";

/**
 * Router state passed from the Manage Devices bulk action.
 * `deviceIds` lets us persist per-device edits on submit; `deviceType` and
 * `deviceCount` drive the header copy ("Edit Controls: 2 Thermostats").
 */
export interface EditControlsLocationState {
  deviceIds: string[];
  deviceType: string;
  deviceCount: number;
}

export type EditControlsSystemMode = "cool" | "heat" | "auto" | "off";
export type EditControlsFanMode = "auto" | "on" | "circulate";

export interface EditControlsFormValues {
  systemMode: EditControlsSystemMode;
  fanMode: EditControlsFanMode;
  /** Whether the user has explicitly set setpoints. `false` = "Mixed" (unset). */
  setpointSet: boolean;
  /** Heat setpoint in degrees. Used when systemMode is `heat` or `auto`. */
  heatSetpoint: number;
  /** Cool setpoint in degrees. Used when systemMode is `cool` or `auto`. */
  coolSetpoint: number;
}

const DEFAULT_VALUES: EditControlsFormValues = {
  systemMode: "cool",
  fanMode: "auto",
  setpointSet: false,
  heatSetpoint: 68,
  coolSetpoint: 72,
};

// Hardware-representative bounds for thermostat setpoints. Used only as a
// fallback when the per-device configuration hasn't been fetched yet; live
// device bounds derived from the RIS configuration response take priority.
const SETPOINT_MIN = 40;
const SETPOINT_MAX = 99;

const SYSTEM_MODES: EditControlsSystemMode[] = ["cool", "heat", "auto", "off"];
const FAN_MODES: EditControlsFanMode[] = ["auto", "on", "circulate"];

// Reads a single device's `SystemSwitchCapabilities` array from any of the
// shapes the RIS configuration response has been observed to use.
const readSystemSwitchCapabilities = (obj: unknown): string[] => {
  if (obj == null || typeof obj !== "object") return [];
  const rec = obj as Record<string, unknown>;
  const candidates: unknown[] = [
    (rec.Reported as Record<string, unknown> | undefined)?.SystemSwitchCapabilities,
    (rec.Desired as Record<string, unknown> | undefined)?.SystemSwitchCapabilities,
    rec.SystemSwitchCapabilities,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) {
      return c.filter((v): v is string => typeof v === "string");
    }
  }
  return [];
};

function ChevronIcon({ direction }: { direction: "up" | "down" }) {
  const d = direction === "up" ? "M4 10l4-4 4 4" : "M4 6l4 4 4-4";
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d={d}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface SetpointStepperProps {
  label: string;
  value: number;
  variant: "heat" | "cool";
  min: number;
  max: number;
  decrementLabel: string;
  incrementLabel: string;
  onChange: (delta: number) => void;
  testIdPrefix: string;
}

function SetpointStepper({
  label,
  value,
  variant,
  min,
  max,
  decrementLabel,
  incrementLabel,
  onChange,
  testIdPrefix,
}: SetpointStepperProps) {
  const valueClass =
    variant === "heat"
      ? `${styles.setpointStepperValue} ${styles.setpointStepperValueHeat}`
      : `${styles.setpointStepperValue} ${styles.setpointStepperValueCool}`;
  return (
    <div className={styles.setpointStepper}>
      <div className={styles.setpointStepperLabel}>{label}</div>
      <div className={styles.setpointStepperControls}>
        <Button
          variant="secondary"
          className={styles.setpointStepperButton}
          onClick={() => onChange(-1)}
          aria-label={decrementLabel}
          data-testid={`${testIdPrefix}-decrement`}
          disabled={value <= min}
        >
          <ChevronIcon direction="down" />
        </Button>
        <span className={valueClass} data-testid={`${testIdPrefix}-value`}>
          {value}
        </span>
        <Button
          variant="secondary"
          className={styles.setpointStepperButton}
          onClick={() => onChange(1)}
          aria-label={incrementLabel}
          data-testid={`${testIdPrefix}-increment`}
          disabled={value >= max}
        >
          <ChevronIcon direction="up" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Test-only helper — cleared per test to reset the per-instance
 * configuration-request cache that the component uses to dedupe
 * StrictMode's dev double-mount. Kept as a stable no-op in production
 * because the cache now lives on the component instance (via a `useRef`)
 * rather than at module scope, so nothing needs to be flushed between
 * runs — the ref is simply re-created on each real remount.
 */
export const __resetThermostatConfigurationCacheForTests = () => {
  // Intentionally no-op — retained for backward compatibility with existing
  // test setups.
};

export const EditControls = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Router state is optional (a user could deep-link into the page). When
  // absent we fall back to zero-count so the page still renders and the
  // Back button routes to the devices list.
  const state = (location.state ?? null) as EditControlsLocationState | null;
  const deviceCount = state?.deviceCount ?? 0;
  const deviceType = state?.deviceType ?? "Thermostat";

  const [values, setValues] = useState<EditControlsFormValues>(DEFAULT_VALUES);
  // Which controls the user explicitly interacted with. Setpoint uses the
  // existing `values.setpointSet` flag ("Mixed" → "Set" toggle) as its
  // touched signal; systemMode / fanMode need a companion boolean because
  // Cool / Auto happen to be their defaults and we can't distinguish
  // "opened form, didn't touch" from "picked the default" without one.
  const [touched, setTouched] = useState<{
    systemMode?: boolean;
    fanMode?: boolean;
  }>({});
  const [view, setView] = useState<"edit" | "review">("edit");
  const [isApplying, setIsApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  // Per-device configuration keyed by deviceId. Populated by the fetch
  // effect below; drives the derived setpoint bounds so the Heat / Cool
  // steppers can't exceed what the selected devices actually support.
  const [thermostatConfigs, setThermostatConfigs] = useState<
    Record<string, ThermostatConfigurationResponseV2>
  >({});

  // Fetch each selected thermostat's current configuration when the page
  // opens. A per-instance ref-based cache dedupes requests across
  // StrictMode's dev double-invocation of effects (which would otherwise
  // fire two identical network calls) but is intentionally scoped to this
  // component instance, so navigating away and back triggers a fresh
  // fetch — no stale data lingers across page visits.
  const deviceIdsKey = (state?.deviceIds ?? []).join(",");
  const configurationRequestsRef = useRef<
    Map<string, Promise<ThermostatConfigurationResponseV2>>
  >(new Map());
  useEffect(() => {
    const deviceIds = state?.deviceIds ?? [];
    if (deviceIds.length === 0 || deviceType !== "Thermostat") return;

    let cancelled = false;
    // Fire in parallel; per-device catch so one failure doesn't hide the rest.
    for (const id of deviceIds) {
      let request = configurationRequestsRef.current.get(id);
      if (!request) {
        request = manageDevicesService
          .fetchThermostatConfiguration(id)
          .catch((error) => {
            // Evict rejections so a subsequent effect run (e.g. tab focus)
            // can retry the failed device without touching the successful
            // siblings.
            configurationRequestsRef.current.delete(id);
            throw error;
          });
        configurationRequestsRef.current.set(id, request);
      }
      request
        .then((config) => {
          if (cancelled || !config) return;
          // Surfaced during debugging so the actual response shape can be
          // inspected in the browser console when the derived bounds don't
          // match the expected setpoint floor/ceiling.       
          setThermostatConfigs((prev) => ({ ...prev, [id]: config }));
        })
        .catch((error) => {
          if (cancelled) return;
          console.log(
            `[EditControls] Failed to load thermostat configuration for ${id}:`,
            error,
          );
        });
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceIdsKey, deviceType]);

  // Derive the setpoint bounds shown on the Heat / Cool steppers from the
  // fetched device configurations. Uses the widest usable range for the
  // selection: MIN of the minimums and MAX of the maximums across devices,
  // so the user can pick any value that at least one device supports. Falls
  // back to the hardware defaults until at least one config has arrived.
  const setpointBounds = useMemo(() => {
    const configs = Object.values(thermostatConfigs);
    if (configs.length === 0) {
      return {
        heatMin: SETPOINT_MIN,
        heatMax: SETPOINT_MAX,
        coolMin: SETPOINT_MIN,
        coolMax: SETPOINT_MAX,
        safeHeatMin: SETPOINT_MIN,
        safeHeatMax: SETPOINT_MAX,
        safeCoolMin: SETPOINT_MIN,
        safeCoolMax: SETPOINT_MAX,
        hasConfigs: false,
      };
    }
    // Look for each allowed-setpoint field under `Reported`, `Desired`, or
    // the top level of the response. If none of those hit (the RIS backend
    // has been observed nesting these under other keys in some tenants),
    // fall back to a bounded recursive walk of the object graph. Depth cap
    // keeps this cheap and avoids ever descending into cycles.
    const readNumber = (
      obj: unknown,
      key:
        | "MinimumHeatSetpointAllowed"
        | "MaximumHeatSetpointAllowed"
        | "MinimumCoolSetpointAllowed"
        | "MaximumCoolSetpointAllowed",
    ): number | undefined => {
      if (obj == null || typeof obj !== "object") return undefined;
      const rec = obj as Record<string, unknown>;
      const direct: unknown[] = [
        (rec.Reported as Record<string, unknown> | undefined)?.[key],
        (rec.Desired as Record<string, unknown> | undefined)?.[key],
        rec[key],
      ];
      for (const v of direct) {
        if (typeof v === "number") return v;
      }
      // Recursive fallback: walk plain object children up to 4 levels deep
      // looking for the key. Arrays and non-objects are skipped.
      const seen = new Set<unknown>();
      const walk = (node: unknown, depth: number): number | undefined => {
        if (depth <= 0 || node == null || typeof node !== "object") return undefined;
        if (seen.has(node)) return undefined;
        seen.add(node);
        const child = node as Record<string, unknown>;
        const hit = child[key];
        if (typeof hit === "number") return hit;
        for (const nestedKey of Object.keys(child)) {
          const nested = walk(child[nestedKey], depth - 1);
          if (nested !== undefined) return nested;
        }
        return undefined;
      };
      return walk(rec, 4);
    };
    const pick = (
      key:
        | "MinimumHeatSetpointAllowed"
        | "MaximumHeatSetpointAllowed"
        | "MinimumCoolSetpointAllowed"
        | "MaximumCoolSetpointAllowed",
    ) =>
      configs
        .map((c) => readNumber(c, key))
        .filter((v): v is number => typeof v === "number");
    const heatMins = pick("MinimumHeatSetpointAllowed");
    const heatMaxs = pick("MaximumHeatSetpointAllowed");
    const coolMins = pick("MinimumCoolSetpointAllowed");
    const coolMaxs = pick("MaximumCoolSetpointAllowed");
    const bounds = {
      heatMin: heatMins.length ? Math.min(...heatMins) : SETPOINT_MIN,
      heatMax: heatMaxs.length ? Math.max(...heatMaxs) : SETPOINT_MAX,
      coolMin: coolMins.length ? Math.min(...coolMins) : SETPOINT_MIN,
      coolMax: coolMaxs.length ? Math.max(...coolMaxs) : SETPOINT_MAX,
      // "Safe" range — the tightest floor / ceiling supported by *every*
      // selected device. Chosen values that fall outside this range but
      // inside the widest range trigger the "some thermostats will clamp"
      // warning on the review screen.
      safeHeatMin: heatMins.length ? Math.max(...heatMins) : SETPOINT_MIN,
      safeHeatMax: heatMaxs.length ? Math.min(...heatMaxs) : SETPOINT_MAX,
      safeCoolMin: coolMins.length ? Math.max(...coolMins) : SETPOINT_MIN,
      safeCoolMax: coolMaxs.length ? Math.min(...coolMaxs) : SETPOINT_MAX,
      hasConfigs: configs.length > 0,
    };
    console.debug("[EditControls] Derived setpoint bounds", {
      configCount: configs.length,
      heatMins,
      heatMaxs,
      coolMins,
      coolMaxs,
      bounds,
    });
    return bounds;
  }, [thermostatConfigs]);

  // If the derived bounds shrink below the current setpoint (e.g. defaults
  // seeded 72 but the selected devices cap at 68), re-clamp so the value
  // stays within the newly-loaded range.
  useEffect(() => {
    setValues((prev) => {
      const heat = Math.max(
        setpointBounds.heatMin,
        Math.min(setpointBounds.heatMax, prev.heatSetpoint),
      );
      const cool = Math.max(
        setpointBounds.coolMin,
        Math.min(setpointBounds.coolMax, prev.coolSetpoint),
      );
      if (heat === prev.heatSetpoint && cool === prev.coolSetpoint) return prev;
      return { ...prev, heatSetpoint: heat, coolSetpoint: cool };
    });
  }, [
    setpointBounds.heatMin,
    setpointBounds.heatMax,
    setpointBounds.coolMin,
    setpointBounds.coolMax,
  ]);

  // Derive the Fan Mode options from `FanSwitchCapabilities.Positions` on
  // each selected device. For a single device we get its exact list; for
  // multiple devices we take the union so the user sees every value at
  // least one thermostat supports (matches the widest-range pattern used
  // for the setpoint bounds above). API values are title-case ("On",
  // "Auto", "Circulate") and the form uses lower-case enum values.
  const availableFanModes = useMemo<EditControlsFanMode[]>(() => {
    const configs = Object.values(thermostatConfigs);
    if (configs.length === 0) return FAN_MODES;

    const readPositions = (obj: unknown): string[] => {
      if (obj == null || typeof obj !== "object") return [];
      const rec = obj as Record<string, unknown>;
      const candidates: unknown[] = [
        (rec.Reported as Record<string, unknown> | undefined)?.FanSwitchCapabilities,
        (rec.Desired as Record<string, unknown> | undefined)?.FanSwitchCapabilities,
        rec.FanSwitchCapabilities,
      ];
      for (const c of candidates) {
        if (c && typeof c === "object") {
          const positions = (c as Record<string, unknown>).Positions;
          if (Array.isArray(positions)) {
            return positions.filter((v): v is string => typeof v === "string");
          }
        }
      }
      return [];
    };

    const union = new Set<EditControlsFanMode>();
    for (const c of configs) {
      for (const raw of readPositions(c)) {
        const lc = raw.toLowerCase();
        if (lc === "auto" || lc === "on" || lc === "circulate") {
          union.add(lc);
        }
      }
    }
    if (union.size === 0) return FAN_MODES;
    // Preserve the stable presentation order established by FAN_MODES so
    // the dropdown doesn't reorder itself when the selection changes.
    return FAN_MODES.filter((m) => union.has(m));
  }, [thermostatConfigs]);

  // Same pattern as `availableFanModes` but for the System Mode dropdown —
  // union of `SystemSwitchCapabilities` across selected devices. The API
  // schema also emits "EmergencyHeat" which the form doesn't model, so it
  // is intentionally dropped rather than mapped to plain "heat".
  const availableSystemModes = useMemo<EditControlsSystemMode[]>(() => {
    const configs = Object.values(thermostatConfigs);
    if (configs.length === 0) return SYSTEM_MODES;

    const union = new Set<EditControlsSystemMode>();
    for (const c of configs) {
      for (const raw of readSystemSwitchCapabilities(c)) {
        const lc = raw.toLowerCase();
        if (lc === "cool" || lc === "heat" || lc === "auto" || lc === "off") {
          union.add(lc);
        }
      }
    }
    if (union.size === 0) return SYSTEM_MODES;
    return SYSTEM_MODES.filter((m) => union.has(m));
  }, [thermostatConfigs]);

  // `availableSystemModes` is a UNION across devices, so a mode can be
  // selectable in the dropdown while one or more individual devices still
  // don't support it — those devices ignore the batch op and keep their
  // current mode/setpoint. Warn the user when that's the case for the
  // currently selected mode.
  const systemModeUnsupportedByAnyDevice = useMemo(() => {
    const configs = Object.values(thermostatConfigs);
    if (configs.length === 0) return false;

    return configs.some((c) => {
      const caps = readSystemSwitchCapabilities(c).map((v) => v.toLowerCase());
      // No capability data for this device — nothing to warn about.
      if (caps.length === 0) return false;
      return !caps.includes(values.systemMode);
    });
  }, [thermostatConfigs, values.systemMode]);

  // If the current fan mode isn't in the newly-derived options (e.g. the
  // seeded default is "auto" but the selected devices only advertise
  // "on" / "circulate"), snap to the first available choice.
  useEffect(() => {
    setValues((prev) => {
      if (availableFanModes.includes(prev.fanMode)) return prev;
      const next = availableFanModes[0];
      if (!next || next === prev.fanMode) return prev;
      return { ...prev, fanMode: next };
    });
  }, [availableFanModes]);

  // Same snap for System Mode when the seeded default drops out of the
  // derived options (e.g. defaults to "cool" but the device only advertises
  // "heat" / "off").
  useEffect(() => {
    setValues((prev) => {
      if (availableSystemModes.includes(prev.systemMode)) return prev;
      const next = availableSystemModes[0];
      if (!next || next === prev.systemMode) return prev;
      return { ...prev, systemMode: next };
    });
  }, [availableSystemModes]);

  // Off has no active heating/cooling stage, so any previously-set setpoint
  // is meaningless — clear the flag so it doesn't leak into the review
  // summary or the applied payload.
  useEffect(() => {
    if (values.systemMode !== "off") return;
    setValues((prev) =>
      prev.setpointSet ? { ...prev, setpointSet: false } : prev,
    );
  }, [values.systemMode]);

  const hasChanges =
    Boolean(touched.systemMode) ||
    Boolean(touched.fanMode) ||
    values.setpointSet;

  const headerSubject = useMemo(() => {
    // Resolve the device-type portion of the header (e.g. "Thermostats").
    // Falls back to the raw type string when there is no translation.
    const typeKeyMap: Record<string, string> = {
      Thermostat: "editControls.subjects.thermostats",
      Camera: "editControls.subjects.cameras",
      SecurityPanel: "editControls.subjects.securityPanels",
    };
    const key = typeKeyMap[deviceType];
    return key ? t(key) : deviceType;
  }, [deviceType, t]);

  const handleBack = () => {
    // On the review step, "back" returns to the edit form so the user's
    // selections are preserved (they live in local state).
    if (view === "review") {
      setView("edit");
      return;
    }
    // Prefer browser back so submitted forms can be revisited; fall back to
    // the devices list when there is no history entry (e.g. direct entry).
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/devices");
    }
  };

  const handleCancel = () => {
    navigate("/devices");
  };

  const handleReview = () => {
    setView("review");
  };

  const handleEdit = () => {
    setView("edit");
  };

  const handleApply = async () => {
    // Guard against deep-linked entry with no device ids, no changes to
    // apply, or a request already in flight.
    const deviceIds = state?.deviceIds ?? [];
    if (deviceIds.length === 0 || isApplying || !hasChanges) {
      return;
    }
    setIsApplying(true);
    setApplyError(null);
    try {
      // Only forward the fields the user actually changed. `systemMode` is
      // included whenever setpoint is set, because the service needs it to
      // decide whether to emit the heat, cool, or both setpoint ops.
      const payload: Parameters<
        typeof thermostatBatchService.applyBulkSettings
      >[1] = {};
      if (touched.systemMode) payload.systemMode = values.systemMode;
      if (touched.fanMode) payload.fanMode = values.fanMode;
      if (values.setpointSet) {
        payload.setpointSet = true;
        payload.systemMode = values.systemMode;
        payload.heatSetpoint = values.heatSetpoint;
        payload.coolSetpoint = values.coolSetpoint;
      }
      await thermostatBatchService.applyBulkSettings(deviceIds, payload);
      // Hand the success message off to Manage Devices via router state
      // — that page already renders a top-of-page success toast when it
      // sees `location.state.message`.
      navigate("/devices", {
        state: {
          message: t("editControls.reviewScreen.applySuccess", {
            count: deviceCount,
            subject: headerSubject.toLowerCase(),
          }),
        },
      });
    } catch (error) {
      // Surface a generic message; the SDK's error object isn't localised
      // and often exposes internal fields we don't want to render.
      setApplyError(t("editControls.reviewScreen.applyError"));
    } finally {
      setIsApplying(false);
    }
  };

  // Auto-dismiss the error toast after 5s so it doesn't linger through the
  // user's next attempt.
  useEffect(() => {
    if (!applyError) return;
    const timer = window.setTimeout(() => setApplyError(null), 5000);
    return () => window.clearTimeout(timer);
  }, [applyError]);

  const handleSetpointClick = () => {
    // Toggle from the "Mixed" (unset) placeholder to a set state. The full
    // setpoint editor UI lives in a follow-up story.
    setValues((prev) => ({ ...prev, setpointSet: !prev.setpointSet }));
  };

  const clampToRange = (value: number, min: number, max: number) =>
    Math.max(min, Math.min(max, value));

  const handleHeatChange = (delta: number) => {
    setValues((prev) => ({
      ...prev,
      heatSetpoint: clampToRange(
        prev.heatSetpoint + delta,
        setpointBounds.heatMin,
        setpointBounds.heatMax,
      ),
    }));
  };

  const handleCoolChange = (delta: number) => {
    setValues((prev) => ({
      ...prev,
      coolSetpoint: clampToRange(
        prev.coolSetpoint + delta,
        setpointBounds.coolMin,
        setpointBounds.coolMax,
      ),
    }));
  };

  // Which setpoint stepper(s) to show once the user has committed to setting
  // a value. Cool-only and heat-only modes render just one stepper; Auto
  // renders both; Off falls back to the Mixed / Set toggle since there is
  // no active heating or cooling stage to target.
  const setpointVariant: "none" | "cool" | "heat" | "both" = values.setpointSet
    ? values.systemMode === "auto"
      ? "both"
      : values.systemMode === "cool"
        ? "cool"
        : values.systemMode === "heat"
          ? "heat"
          : "none"
    : "none";
  const showSetpointSteppers = setpointVariant !== "none";

  // Whether the chosen setpoint(s) fall outside the tightest per-device
  // range. When true, at least one selected thermostat will clamp the
  // value to its own configured limit; a warning banner surfaces this on
  // the review screen so the user knows before applying.
  const setpointOutOfSafeRange = useMemo(() => {
    if (!values.setpointSet || !setpointBounds.hasConfigs) return false;
    const affectsHeat =
      values.systemMode === "heat" || values.systemMode === "auto";
    const affectsCool =
      values.systemMode === "cool" || values.systemMode === "auto";
    const heatOutOfSafe =
      affectsHeat &&
      (values.heatSetpoint > setpointBounds.safeHeatMax ||
        values.heatSetpoint < setpointBounds.safeHeatMin);
    const coolOutOfSafe =
      affectsCool &&
      (values.coolSetpoint > setpointBounds.safeCoolMax ||
        values.coolSetpoint < setpointBounds.safeCoolMin);
    // A device that doesn't support the selected mode at all is excluded
    // from the safe/widest bounds above (it never reports setpoint limits
    // for a mode it can't run), so the numeric check alone can miss it.
    // That device won't honor this setpoint either, so surface the same
    // warning for it too.
    return heatOutOfSafe || coolOutOfSafe || systemModeUnsupportedByAnyDevice;
  }, [
    values.setpointSet,
    values.systemMode,
    values.heatSetpoint,
    values.coolSetpoint,
    setpointBounds,
    systemModeUnsupportedByAnyDevice,
  ]);


  // Fields to display in the review summary. Only fields the user actually
  // changed appear — the review is a "changes to apply" list, not a full
  // form snapshot.
  const reviewItems = useMemo(() => {
    const items: Array<{ id: string; label: string; value: string }> = [];
    if (touched.systemMode) {
      items.push({
        id: "systemMode",
        label: t("editControls.fields.systemMode.label"),
        value: t(`editControls.options.systemMode.${values.systemMode}`),
      });
    }
    if (values.setpointSet) {
      let setpointValue: string;
      if (values.systemMode === "auto") {
        setpointValue = t("editControls.setpoint.heatCoolValue", {
          heat: values.heatSetpoint,
          cool: values.coolSetpoint,
        });
      } else if (values.systemMode === "cool") {
        setpointValue = t("editControls.setpoint.coolValue", {
          cool: values.coolSetpoint,
        });
      } else if (values.systemMode === "heat") {
        setpointValue = t("editControls.setpoint.heatValue", {
          heat: values.heatSetpoint,
        });
      } else {
        setpointValue = t("editControls.setpoint.set");
      }
      items.push({
        id: "setpoint",
        label: t("editControls.fields.setpoint.label"),
        value: setpointValue,
      });
    }
    if (touched.fanMode) {
      items.push({
        id: "fanMode",
        label: t("editControls.fields.fanMode.label"),
        value: t(`editControls.options.fanMode.${values.fanMode}`),
      });
    }
    return items;
  }, [t, touched, values]);


  return (
    <div className={styles.container}>
      {applyError && (
        <div
          className={styles.errorToast}
          role="alert"
          data-testid="apply-error"
        >
          <span>{applyError}</span>
          <button
            type="button"
            className={styles.closeToast}
            onClick={() => setApplyError(null)}
            aria-label={t("editControls.ariaLabels.dismissError", {
              defaultValue: "Dismiss error",
            })}
          >
            ×
          </button>
        </div>
      )}
      <div className={styles.contentCard}>
        <header className={styles.headerRow}>
          <Button
            variant="text"
            className={styles.backButton}
            onClick={handleBack}
            aria-label={t("editControls.ariaLabels.back")}
          >
            <img
              src={chevronLeftIcon}
              width="20"
              height="20"
              alt=""
              aria-hidden="true"
            />
          </Button>
          <h1 className={styles.title}>
            {t("editControls.title", {
              count: deviceCount,
              subject: headerSubject,
            })}
          </h1>
        </header>

        {view === "edit" ? (
          <>
            <div className={styles.formBody}>
              <EditControlsRow
                label={t("editControls.fields.systemMode.label")}
                description={t("editControls.fields.systemMode.description")}
                controlId="edit-controls-system-mode"
              >
                <Select
                  id="edit-controls-system-mode"
                  name="systemMode"
                  className={styles.editSelect}
                  value={values.systemMode}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    setTouched((prev) => ({ ...prev, systemMode: true }));
                    setValues((prev) => ({
                      ...prev,
                      systemMode: e.target.value as EditControlsSystemMode,
                    }));
                  }}
                >
                  {availableSystemModes.map((mode) => (
                    <option key={mode} value={mode}>
                      {t(`editControls.options.systemMode.${mode}`)}
                    </option>
                  ))}
                </Select>
                {systemModeUnsupportedByAnyDevice && (
                  <div
                    className={styles.systemModeHint}
                    data-testid="system-mode-partial-support-warning"
                  >
                    <img
                      src={informationLineIcon}
                      width="16"
                      height="16"
                      alt=""
                      aria-hidden="true"
                    />
                    <span>
                      {t("editControls.fields.systemMode.partialSupportWarning", {
                        mode: t(`editControls.options.systemMode.${values.systemMode}`),
                        defaultValue:
                          "Thermostats without {{mode}} will keep their current mode and setpoint",
                      })}
                    </span>
                  </div>
                )}
              </EditControlsRow>

              <EditControlsRow
                label={t("editControls.fields.setpoint.label")}
                description={t("editControls.fields.setpoint.description")}
              >
                {showSetpointSteppers ? (
                  <div
                    className={styles.setpointSteppers}
                    role="group"
                    aria-label={t("editControls.fields.setpoint.label")}
                  >
                    {(setpointVariant === "heat" ||
                      setpointVariant === "both") && (
                        <SetpointStepper
                          label={t("editControls.setpoint.heatTo")}
                          value={values.heatSetpoint}
                          variant="heat"
                          min={setpointBounds.heatMin}
                          max={setpointBounds.heatMax}
                          decrementLabel={t("editControls.setpoint.decreaseHeat")}
                          incrementLabel={t("editControls.setpoint.increaseHeat")}
                          onChange={handleHeatChange}
                          testIdPrefix="heat-setpoint"
                        />
                      )}
                    {(setpointVariant === "cool" ||
                      setpointVariant === "both") && (
                        <SetpointStepper
                          label={t("editControls.setpoint.coolTo")}
                          value={values.coolSetpoint}
                          variant="cool"
                          min={setpointBounds.coolMin}
                          max={setpointBounds.coolMax}
                          decrementLabel={t("editControls.setpoint.decreaseCool")}
                          incrementLabel={t("editControls.setpoint.increaseCool")}
                          onChange={handleCoolChange}
                          testIdPrefix="cool-setpoint"
                        />
                      )}
                  </div>
                ) : (
                  <Button
                    variant="secondary"
                    className={styles.setpointButton}
                    onClick={handleSetpointClick}
                    disabled={values.systemMode === "off"}
                    data-testid="setpoint-toggle-button"
                    aria-disabled={values.systemMode === "off" || undefined}
                  >
                    <span className={styles.setpointStatus}>
                      {t("editControls.setpoint.mixed")}
                    </span>
                    <span className={styles.setpointDivider} aria-hidden="true">
                      –
                    </span>
                    <span className={styles.setpointAction}>
                      {t("editControls.setpoint.action")}
                    </span>
                  </Button>
                )}
              </EditControlsRow>

              <EditControlsRow
                label={t("editControls.fields.fanMode.label")}
                description={t("editControls.fields.fanMode.description")}
                controlId="edit-controls-fan-mode"
              >
                <Select
                  id="edit-controls-fan-mode"
                  name="fanMode"
                  className={styles.editSelect}
                  value={values.fanMode}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    setTouched((prev) => ({ ...prev, fanMode: true }));
                    setValues((prev) => ({
                      ...prev,
                      fanMode: e.target.value as EditControlsFanMode,
                    }));
                  }}
                >
                  {availableFanModes.map((mode) => (
                    <option key={mode} value={mode}>
                      {t(`editControls.options.fanMode.${mode}`)}
                    </option>
                  ))}
                </Select>
              </EditControlsRow>
            </div>

            <footer className={styles.footer}>
              <Button
                variant="text"
                className={styles.cancelLink}
                onClick={handleCancel}
              >
                {t("editControls.cancel")}
              </Button>
              <Button
                variant="primary"
                className={styles.reviewButton}
                onClick={handleReview}
              >
                {t("editControls.review")}
              </Button>
            </footer>
          </>
        ) : (
          <>
            <div className={styles.reviewBody}>
              <div className={styles.reviewHeader}>
                <div className={styles.reviewIntro}>
                  <h2 className={styles.reviewTitle}>
                    {t("editControls.reviewScreen.title")}
                  </h2>
                  <p className={styles.reviewDescription}>
                    {t("editControls.reviewScreen.description", {
                      count: deviceCount,
                      subject: headerSubject.toLowerCase(),
                    })}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  className={styles.reviewEditButton}
                  onClick={handleEdit}
                >
                  {t("editControls.reviewScreen.edit")}
                </Button>
              </div>

              {touched.systemMode && systemModeUnsupportedByAnyDevice && (
                <div
                  className={styles.setpointWarningBanner}
                  role="alert"
                  data-testid="system-mode-partial-support-warning"
                >
                  <img
                    className={styles.setpointWarningIcon}
                    src={informationLineIcon}
                    width="18"
                    height="18"
                    alt=""
                    aria-hidden="true"
                  />
                  <span>
                    {t("editControls.fields.systemMode.partialSupportWarning", {
                      mode: t(`editControls.options.systemMode.${values.systemMode}`),
                      defaultValue:
                        "Thermostats without {{mode}} will keep their current mode and setpoint",
                    })}
                  </span>
                </div>
              )}

              {setpointOutOfSafeRange && (
                <div
                  className={styles.setpointWarningBanner}
                  role="alert"
                  data-testid="setpoint-warning-banner"
                >
                  <img
                    className={styles.setpointWarningIcon}
                    src={informationLineIcon}
                    width="18"
                    height="18"
                    alt=""
                    aria-hidden="true"
                  />
                  <span>
                    {t(
                      "editControls.reviewScreen.setpointOutOfRangeWarning",
                      {
                        defaultValue:
                          "This setpoint is outside some thermostats' configured min / max — it will be clamped to their nearest limit.",
                      },
                    )}
                  </span>
                </div>
              )}

              <div className={styles.reviewSummary}>
                {reviewItems.length === 0 ? (
                  <div
                    className={styles.reviewEmpty}
                    data-testid="review-no-changes"
                  >
                    {t("editControls.reviewScreen.noChanges")}
                  </div>
                ) : (
                  reviewItems.map((item) => (
                    <div key={item.id} className={styles.reviewItem}>
                      <div className={styles.reviewItemLabel}>{item.label}</div>
                      <div className={styles.reviewItemValue}>{item.value}</div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <footer className={styles.footer}>
              <Button
                variant="text"
                className={styles.cancelLink}
                onClick={handleEdit}
                disabled={isApplying}
              >
                {t("editControls.reviewScreen.back")}
              </Button>
              <Button
                variant="primary"
                className={styles.reviewButton}
                onClick={handleApply}
                disabled={isApplying || !hasChanges}
              >
                {isApplying
                  ? t("editControls.reviewScreen.applying")
                  : t("editControls.reviewScreen.apply")}
              </Button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
};

export default EditControls;
